/**
 * LiveStreamAuth Remapper
 *
 * Bridges Cloud Function wire JSON ↔ `LiveStreamAuthDto` ↔ `LiveStreamAuthEntity`,
 * following the `fromJson(): Dto` + `fromDto(): Entity` convention codified by
 * `notificationInfoRemapper.ts` / `chatMessageRemapper.ts` and `data-layer.md`.
 *
 * The raw JSON is the `response.data.data` object of `generateLiveStreamToken`;
 * the wire fields are literally `appId` / `token` (Flutter hand-builds the model
 * with no `@JsonKey`, so each field equals its Dart property name).
 *
 * Flutter source: lib/data/models/live_stream_auth.dart:1-6,
 * lib/services/cloud_functions_service.dart:89-91
 */

import type { LiveStreamAuthDto } from '@/data/dtos/liveStreamAuth/liveStreamAuthDto';
import type { JsonObject } from '@/data/types/json';
import type { LiveStreamAuthEntity } from '@/domain/entities/liveStreamAuth/liveStreamAuthEntity';

export class LiveStreamAuthRemapper {
  /**
   * Parse raw Cloud Function JSON into a `LiveStreamAuthDto`. The only entry
   * point from raw JSON. Defensive against partial payloads: `appId` falls back
   * to `''` when absent; `token` stays `undefined` when absent (Flutter's
   * `token` is nullable).
   */
  static fromJson(data: JsonObject): LiveStreamAuthDto {
    return {
      appId: (data['appId'] as string | undefined) || '',
      token: data['token'] as string | undefined,
    };
  }

  /**
   * Build a `LiveStreamAuthEntity` from a `LiveStreamAuthDto`. The shapes are
   * identical today, so the fields assign through directly.
   */
  static fromDto(dto: LiveStreamAuthDto): LiveStreamAuthEntity {
    return {
      appId: dto.appId,
      token: dto.token,
    };
  }
}
